import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const STATUS_META = {
  pending: { label: 'Pendente', emoji: '🕐', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  accepted: { label: 'Aceito', emoji: '✅', color: 'var(--nc-primary)', bg: 'var(--nc-success-soft)' },
  picked_up: { label: 'Em entrega', emoji: '🛵', color: '#1e4d8c', bg: '#eff6ff' },
  delivered: { label: 'Entregue', emoji: '📦', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  cancelled: { label: 'Cancelado', emoji: '🚫', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  declined: { label: 'Recusado', emoji: '✋', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
};

// Próxima ação do entregador para cada status (LE-015)
const NEXT_ACTION = {
  pending: { status: 'accepted', label: 'Aceitar entrega', confirm: 'Aceitar este pedido de entrega?' },
  accepted: { status: 'picked_up', label: 'Retirar item', confirm: 'Confirmar que o item foi retirado?' },
  picked_up: { status: 'delivered', label: 'Confirmar entrega', confirm: 'Confirmar que a entrega foi concluída?' },
};

// LE-024: polling da fila do entregador — atualização automática sem reload.
// Intervalo conservador de 30s (rate limit público é 1000/15min por endpoint;
// 2 req/min fica muito abaixo). Pausa quando a aba está oculta e nunca
// sobrepõe uma requisição em voo.
const POLL_INTERVAL_MS = 30000;

export function DeliveryOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pool, setPool] = useState([]);
  const [hasProfile, setHasProfile] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lastSync, setLastSync] = useState(null); // LE-024: "Atualizado às HH:MM"
  const [syncWarning, setSyncWarning] = useState(false); // LE-024: falha de poll silencioso
  // LE-030: ordenação da fila (pendência LE-017 §11) — 'newest' (padrão, mais
  // recentes primeiro) ou 'oldest' (FIFO: pedidos que esperam há mais tempo)
  const [poolSort, setPoolSort] = useState('newest');
  const poolRef = useRef(null); // LE-024: fila do último sync (null = 1º load; detecta novidades)

  // LE-024: `silent` = refresh automático (sem spinner/erro em tela cheia; dados
  // antigos permanecem se a rede falhar). Ação do usuário/primeiro load = não-silent.
  const fetchOrders = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      // LE-030: a fila aceita ?sort=oldest|newest (backend com whitelist). Só
      // envia a query quando NÃO é o default (newest) — preserva o request
      // /deliveries/orders original no comportamento padrão (compatibilidade
      // total com mocks e contratos existentes).
      const qs = poolSort === 'oldest' ? '?sort=oldest' : '';
      const { data } = await api.get(`/deliveries/orders${qs}`);
      // Poll silencioso com sucesso = dados frescos — limpa erro de um load
      // inicial que tenha falhado (senão a tela ficaria presa no erro).
      if (silent) {
        setError(null);
        setSyncWarning(false);
      }
      setOrders(data.data || []);
      // LE-017: fila de pedidos recusados disponíveis para re-aceite
      // LE-024: marca como "Novo" (chip 🆕) apenas os pedidos que entraram na
      // fila APÓS o primeiro load — poolRef começa null e o 1º sync só
      // popula a referência, sem marcar itens pré-existentes como novos.
      const nextPool = (data.pool || []).map(o => {
        if (poolRef.current === null) return { ...o, isNew: false };
        return { ...o, isNew: !poolRef.current.some(p => p.id === o.id) };
      });
      setPool(nextPool);
      poolRef.current = nextPool;
      setHasProfile(data.hasProfile !== false);
      setLastSync(new Date());
    } catch (err) {
      // Refresh automático falhou: mantém o que está na tela e informa com leveza
      if (silent) {
        setSyncWarning(true);
      } else {
        setError(err.response?.data?.error?.message || 'Erro ao carregar pedidos');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [poolSort]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // LE-024: polling — só com perfil de entregador; pausa com a aba oculta;
  // ao voltar a ficar visível, sincroniza na hora.
  useEffect(() => {
    if (hasProfile === false) return undefined;

    let inFlight = false;
    const sync = async () => {
      if (document.visibilityState === 'hidden' || inFlight) return;
      inFlight = true;
      try {
        await fetchOrders({ silent: true });
      } finally {
        inFlight = false;
      }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') sync(); };

    const timer = setInterval(sync, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchOrders, hasProfile]);

  const handleAction = async (order) => {
    const action = NEXT_ACTION[order.status];
    if (!action) return;
    if (!window.confirm(action.confirm)) return;
    setUpdatingId(order.id);
    setError(null);
    try {
      await api.post(`/deliveries/orders/${order.id}/status`, { status: action.status });
      await fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao atualizar o pedido');
    } finally {
      setUpdatingId(null);
    }
  };

  // LE-016: entregador recusa pedido ainda pendente (só pending pode ser recusado)
  const handleDecline = async (order) => {
    if (!window.confirm('Recusar este pedido de entrega?')) return;
    setUpdatingId(order.id);
    setError(null);
    try {
      await api.post(`/deliveries/orders/${order.id}/decline`);
      setNotice('Pedido recusado — voltou para a fila para outro entregador.');
      await fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao recusar o pedido');
    } finally {
      setUpdatingId(null);
    }
  };

  // LE-017: aceita um pedido da fila (recusado por outro entregador)
  const handleClaim = async (order) => {
    if (!window.confirm(`Aceitar este pedido da fila (#${order.id})?`)) return;
    setUpdatingId(order.id);
    setError(null);
    try {
      await api.post(`/deliveries/orders/${order.id}/accept`);
      setNotice('Pedido aceito da fila! Agora é sua responsabilidade.');
      await fetchOrders();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao aceitar o pedido');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatMoney = (v) => (v == null ? null : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);

  const fmtDateTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  // LE-018: linha do tempo do pedido (eventos vindos do backend, ordenados)
  const renderTimeline = (o) => {
    if (!o.events || o.events.length === 0) return null;
    return (
      <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
        {o.events.map((e, i) => (
          <li key={`${e.at}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0' }}>
            <span style={{ fontSize: 11 }}>{e.emoji}</span>
            <span style={{ fontSize: 12, color: 'var(--nc-text-strong)', flex: 1 }}>{e.label}</span>
            <span style={{ fontSize: 10, color: 'var(--nc-text-muted)' }}>{fmtDateTime(e.at)}</span>
          </li>
        ))}
      </ol>
    );
  };

  return (
    <div className="nc-page nc-page--pad">
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'var(--nc-surface)', borderBottom: '1px solid var(--nc-border)', padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <button
          onClick={() => navigate('/perfil')}
          aria-label="Voltar"
          style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={{ fontSize: 16, fontWeight: 600, color: 'var(--nc-text)', margin: 0 }}>Meus pedidos de entrega</h1>
        {/* LE-024: indicador de atualização automática (polling 30s) */}
        {hasProfile !== false && lastSync && !loading && (
          <div style={{ fontSize: 10, color: 'var(--nc-text-muted)', marginTop: 2 }}>
            🔄 Atualizado às {lastSync.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {loading ? <Loading message="Carregando pedidos..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13
          }}>
            {error}
            <button onClick={fetchOrders} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
          </div>
        ) : !hasProfile ? (
          <EmptyState
            icon="🛵"
            title="Crie seu perfil de entregador"
            description="Para receber pedidos de entrega, primeiro crie seu perfil de entregador."
            action={
              <button className="nc-btn-primary" onClick={() => navigate('/entregador/perfil')}>Criar perfil</button>
            }
          />
        ) : orders.length === 0 && pool.length === 0 ? (
          <EmptyState
            icon="📦"
            title="Nenhum pedido atribuído"
            description="Quando alguém solicitar uma entrega com você, o pedido aparecerá aqui."
            action={
              <button className="nc-btn-primary" onClick={() => navigate('/entregas')}>Ver entregas</button>
            }
          />
        ) : (
          <>
            {notice && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)',
                color: 'var(--nc-primary)', fontSize: 12, marginBottom: 12
              }}>
                ✅ {notice}
              </div>
            )}
            {/* LE-024: falha transitória do polling silencioso — aviso neutro, não verde */}
            {syncWarning && (
              <div role="status" style={{
                padding: 10, borderRadius: 10, background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)',
                color: 'var(--nc-warning-text)', fontSize: 12, marginBottom: 12
              }}>
                ⚠️ Falha ao atualizar pedidos. Tentando novamente...
              </div>
            )}
            {orders.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {orders.map(o => {
                  const st = STATUS_META[o.status] || STATUS_META.pending;
                  const action = NEXT_ACTION[o.status];
                  return (
                    <div key={o.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', flex: 1 }}>
                          Pedido #{o.id}{o.business_name ? ` • ${o.business_name}` : ''}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 10 }}>
                          {st.emoji} {st.label}
                        </span>
                      </div>
                      {o.customer_name && (
                        <div className="nc-hint" style={{ marginBottom: 2 }}>👤 Cliente: {o.customer_name}</div>
                      )}
                      <div className="nc-hint" style={{ marginBottom: 2 }}>📍 {o.delivery_address}</div>
                      {o.pickup_address && (
                        <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>🛍️ Retirada: {o.pickup_address}</div>
                      )}
                      {o.notes && (
                        <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>📝 {o.notes}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <span className="nc-caption">
                          {new Date(o.created_at).toLocaleDateString('pt-BR')} {new Date(o.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {o.total_amount != null && (
                          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: 'var(--nc-primary)' }}>{formatMoney(o.total_amount)}</span>
                        )}
                      </div>
                      {o.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button
                            onClick={() => handleAction(o)}
                            disabled={updatingId === o.id}
                            className="nc-btn-primary"
                            style={{ flex: 1, opacity: updatingId === o.id ? 0.7 : 1 }}
                          >
                            {updatingId === o.id ? 'Atualizando...' : NEXT_ACTION.pending.label}
                          </button>
                          <button
                            onClick={() => handleDecline(o)}
                            disabled={updatingId === o.id}
                            style={{
                              flex: 1, padding: '9px 12px', borderRadius: 8,
                              background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', border: '1px solid var(--nc-danger-border)',
                              fontSize: 13, fontWeight: 600, cursor: updatingId === o.id ? 'wait' : 'pointer'
                            }}
                          >
                            {updatingId === o.id ? 'Atualizando...' : 'Recusar entrega'}
                          </button>
                        </div>
                      )}
                      {action && o.status !== 'pending' && (
                        <button
                          onClick={() => handleAction(o)}
                          disabled={updatingId === o.id}
                          className="nc-btn-primary"
                          style={{ marginTop: 10, width: '100%', opacity: updatingId === o.id ? 0.7 : 1 }}
                        >
                          {updatingId === o.id ? 'Atualizando...' : action.label}
                        </button>
                      )}
                      {/* LE-018: histórico expansível (só quando há eventos) */}
                      {o.events && o.events.length > 0 && (
                        <button
                          onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                          aria-expanded={expandedId === o.id}
                          style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--nc-primary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                        >
                          {expandedId === o.id ? '▲ Ocultar histórico' : '▼ Ver histórico'}
                        </button>
                      )}
                      {expandedId === o.id && renderTimeline(o)}
                    </div>
                  );
                })}
              </div>
            )}
            {pool.length > 0 && (
              <div style={{ marginTop: orders.length > 0 ? 18 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', margin: 0, flex: 1 }}>
                    Pedidos disponíveis na fila
                  </h2>
                  {/* LE-030: ordenação da fila por antiguidade */}
                  <div role="group" aria-label="Ordenar fila" style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => setPoolSort('newest')}
                      disabled={poolSort === 'newest'}
                      aria-pressed={poolSort === 'newest'}
                      style={{
                        padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: poolSort === 'newest' ? '#0d5c3b' : '#fff',
                        color: poolSort === 'newest' ? '#fff' : '#6b7280',
                        border: '1px solid', borderColor: poolSort === 'newest' ? '#0d5c3b' : 'var(--nc-border)',
                      }}
                    >
                      Mais recentes
                    </button>
                    <button
                      onClick={() => setPoolSort('oldest')}
                      disabled={poolSort === 'oldest'}
                      aria-pressed={poolSort === 'oldest'}
                      style={{
                        padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: poolSort === 'oldest' ? '#0d5c3b' : '#fff',
                        color: poolSort === 'oldest' ? '#fff' : '#6b7280',
                        border: '1px solid', borderColor: poolSort === 'oldest' ? '#0d5c3b' : 'var(--nc-border)',
                      }}
                    >
                      Mais antigos
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pool.map(o => {
                    return (
                      <div key={o.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px dashed var(--nc-text-muted)', padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', flex: 1 }}>
                            Pedido #{o.id}{o.business_name ? ` • ${o.business_name}` : ''}
                          </span>
                          {/* LE-024: pedido que entrou na fila desde o último sync */}
                          {o.isNew && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, color: 'var(--nc-primary)', background: '#d1fae5',
                              padding: '2px 8px', borderRadius: 10, animation: 'nc-pulse-badge 2s infinite',
                            }}>
                              🆕 Novo
                            </span>
                          )}
                          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--nc-warning-text)', background: 'var(--nc-warning-soft)', padding: '2px 8px', borderRadius: 10 }}>
                            ✋ Aguardando entregador
                          </span>
                        </div>
                        {o.customer_name && (
                          <div className="nc-hint" style={{ marginBottom: 2 }}>👤 Cliente: {o.customer_name}</div>
                        )}
                        <div className="nc-hint" style={{ marginBottom: 2 }}>📍 {o.delivery_address}</div>
                        {o.pickup_address && (
                          <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>🛍️ Retirada: {o.pickup_address}</div>
                        )}
                        {o.notes && (
                          <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginBottom: 2 }}>📝 {o.notes}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          <span className="nc-caption">
                            {new Date(o.created_at).toLocaleDateString('pt-BR')} {new Date(o.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {o.total_amount != null && (
                            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: 'var(--nc-primary)' }}>{formatMoney(o.total_amount)}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleClaim(o)}
                          disabled={updatingId === o.id}
                          className="nc-btn-primary"
                          style={{ marginTop: 10, width: '100%', opacity: updatingId === o.id ? 0.7 : 1 }}
                        >
                          {updatingId === o.id ? 'Aceitando...' : 'Aceitar pedido'}
                        </button>
                        {o.events && o.events.length > 0 && (
                          <button
                            onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                            aria-expanded={expandedId === o.id}
                            style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--nc-primary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                          >
                            {expandedId === o.id ? '▲ Ocultar histórico' : '▼ Ver histórico'}
                          </button>
                        )}
                        {expandedId === o.id && renderTimeline(o)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
